import { create } from 'zustand'
import type { AlertStreamStatus } from './alertStreamClient.ts'

/** `alert` 는 긴급·낙상 스트림, `bio` 는 생체 스트림 */
export type AlertStreamKind = 'alert' | 'bio'

/** 연결이 끊긴 뒤 폴링으로 복귀하기까지의 유예. 짧은 재접속에서는 폴링을 켜지 않는다 */
export const POLLING_FALLBACK_GRACE_MS = 3000

interface AlertStreamState {
    /** true 면 해당 알림 훅이 기존 `refetchInterval` 폴링을 쓴다 */
    polling: Record<AlertStreamKind, boolean>
}

/**
 * 스트림이 살아 있는 동안만 폴링을 끈다.
 * 기본값이 폴링이므로 스트림을 시작하지 않는 경로(로컬 개발 플래그, 시작 전)는 기존 동작 그대로다.
 */
export const useAlertStreamStore = create<AlertStreamState>()(() => ({
    polling: { alert: true, bio: true }
}))

const graceTimers: Partial<Record<AlertStreamKind, ReturnType<typeof setTimeout>>> = {}

const setPolling = (kind: AlertStreamKind, polling: boolean) => {
    if (useAlertStreamStore.getState().polling[kind] === polling) return
    useAlertStreamStore.setState((state) => ({ polling: { ...state.polling, [kind]: polling } }))
}

const clearGrace = (kind: AlertStreamKind) => {
    const timer = graceTimers[kind]
    if (timer) clearTimeout(timer)
    delete graceTimers[kind]
}

/**
 * 스트림 상태를 폴링 여부로 옮긴다.
 * - connected: 즉시 폴링을 끈다.
 * - unavailable·denied·stopped: 기다릴 이유가 없으므로 즉시 폴링한다.
 * - connecting·retrying: 유예 뒤에도 연결되지 않으면 폴링으로 복귀한다.
 */
export const reportAlertStreamStatus = (kind: AlertStreamKind, status: AlertStreamStatus) => {
    if (status === 'connected') {
        clearGrace(kind)
        setPolling(kind, false)
        return
    }

    if (status === 'unavailable' || status === 'denied' || status === 'stopped') {
        clearGrace(kind)
        setPolling(kind, true)
        return
    }

    if (useAlertStreamStore.getState().polling[kind] || graceTimers[kind]) return
    graceTimers[kind] = setTimeout(() => {
        delete graceTimers[kind]
        setPolling(kind, true)
    }, POLLING_FALLBACK_GRACE_MS)
}

/** 알림 훅이 폴링해야 하는지 */
export const useAlertStreamPolling = (kind: AlertStreamKind): boolean =>
    useAlertStreamStore((state) => state.polling[kind])

export const resetAlertStreamStoreForTests = () => {
    const kinds = Object.keys(graceTimers) as AlertStreamKind[]
    kinds.forEach(clearGrace)
    useAlertStreamStore.setState({ polling: { alert: true, bio: true } })
}
